import React from 'react';
import * as Sentry from "@sentry/react";
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { AuthRoutes } from './routes/AuthRoutes';
import { OpenAPI } from "./api/core/OpenAPI";
import { Chat } from "./components/Chat";
import { OnboardingChat } from "./components/OnboardingChat";
import Home from "./pages/Home";
import MemoriesPage from "./pages/Memories";
import ResourcesPage from "./pages/Resources";
import DailyMessageDetailPage from "./pages/DailyMessageDetail";
import ResearchDetailPage from "./pages/ResearchDetail";
import AccountConnections from "./pages/AccountConnections";
import TylerTriggs from "./pages/TylerTriggs";
import NotFound from "./pages/NotFound";
import { DemoTourChat, TourContext } from "./components/demo/DemoTourChat";
import { ErrorFallback } from './components/ErrorFallback';
import { Navigation, NavigationContext } from './components/Navigation';
import { getCsrfTokenSync } from "./utils/csrf";


// Layout component that wraps all routes
const Layout = ({ children, context = NavigationContext.ReactGeneral }: { children: React.ReactNode, context?: NavigationContext }) => {
  return (
    <>
      <Navigation context={context} />
      <div>
        {children}
      </div>
    </>
  );
};

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      {/* Home page - public */}
      <Route path="/" element={<Layout><Home /></Layout>} />

      {/* Tyler Triggs page - public */}
      <Route path="/tyler-triggs" element={<Layout><TylerTriggs /></Layout>} />

      {/* Public auth routes - no authentication check */}
      <Route path="/auth/*" element={<Layout><AuthRoutes /></Layout>} />

      {/* Protected routes */}
      <Route path="/chat" element={
        <Layout>
          <ProtectedRoute redirectTo="/auth/login"><Chat /></ProtectedRoute>
        </Layout>
      } />

      {/* GUEST Demo Tour */}
      <Route path="/guest-tour" element={
        <Layout context={NavigationContext.ReactDemoTour}>
          <DemoTourChat context={TourContext.GuestTour} />
        </Layout>
      } />
      {/* Demo Tour - protected */}
      <Route path="/demo-tour" element={
        <Layout context={NavigationContext.ReactDemoTour}>
          <ProtectedRoute redirectTo="/auth/login"><DemoTourChat context={TourContext.NewUserTour} /></ProtectedRoute>
        </Layout>
      } />

      <Route path="/onboarding" element={
        <Layout>
          <ProtectedRoute redirectTo="/auth/login"><OnboardingChat /></ProtectedRoute>
        </Layout>
      } />
      <Route path="/memories" element={
        <Layout>
          <ProtectedRoute redirectTo="/auth/login"><MemoriesPage /></ProtectedRoute>
        </Layout>
      } />

      {/* Resources routes */}
      <Route path="/resources" element={
        <Layout>
          <ProtectedRoute redirectTo="/auth/login"><ResourcesPage /></ProtectedRoute>
        </Layout>
      } />
      <Route path="/resources/daily-message/:id" element={
        <Layout>
          <ProtectedRoute redirectTo="/auth/login"><DailyMessageDetailPage /></ProtectedRoute>
        </Layout>
      } />
      <Route path="/resources/research/:id" element={
        <Layout>
          <ProtectedRoute redirectTo="/auth/login"><ResearchDetailPage /></ProtectedRoute>
        </Layout>
      } />

      {/* Account connections routes (both with and without trailing slash) */}
      <Route path="/accounts/3rdparty" element={
        <Layout context={NavigationContext.DjangoTemplate}>
          <ProtectedRoute redirectTo="/auth/login"><AccountConnections /></ProtectedRoute>
        </Layout>
      } />
      <Route path="/accounts/3rdparty/" element={
        <Layout context={NavigationContext.DjangoTemplate}>
          <ProtectedRoute redirectTo="/auth/login"><AccountConnections /></ProtectedRoute>
        </Layout>
      } />

      {/* 404 route */}
      <Route path="*" element={<Layout><NotFound /></Layout>} />
    </Routes>
  );
}

const App: React.FC = () => {
  // Use the shared CSRF utility instead of cookie.parse
  const csrftoken = getCsrfTokenSync();

  Sentry.init({
    dsn: window.SENTRY_DSN,
    release: window.COMMIT_SHA,
    beforeSend(event) {
      // anti-fingerprinting extension errors are not related to the app
      if (event.breadcrumbs?.some(breadcrumb =>
        breadcrumb.message?.includes('antifingerprint not defined')
      )) {
        return null;
      }

      return event;
    },
  });

  OpenAPI.interceptors.request.use((request) => {
    if (!request.headers) {
      request.headers = {};
    }

    if (csrftoken) {
      request.headers["X-CSRFToken"] = csrftoken;
    }

    if (
      request.method === "POST" ||
      request.method === "PUT" ||
      request.method === "PATCH"
    ) {
      request.headers["Content-Type"] = "application/json";
    }

    request.withCredentials = true;

    return request;
  });

  return (
    <AuthProvider>
      <Sentry.ErrorBoundary fallback={(errorData) => <ErrorFallback error={errorData.error as Error} resetError={errorData.resetError} />}>
        <Router>
          <AppRoutes />
        </Router>
      </Sentry.ErrorBoundary>
    </AuthProvider>
  );
};

export default App;
